"use client";

import { forwardRef, useId } from "react";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "@/lib/utils";

interface InputProps extends React.ComponentProps<"input"> {
  /** Visible label text */
  label?: string;
  /** Helper text below the input */
  helperText?: string;
  /** Error message — replaces helperText when present */
  error?: string;
  /** Show "Optional" indicator next to label */
  optional?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      label,
      helperText,
      error,
      optional,
      id: externalId,
      "aria-describedby": externalDescribedBy,
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const id = externalId || autoId;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;

    const describedBy = [
      externalDescribedBy,
      error ? errorId : helperText ? helperId : undefined,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

    const inputElement = (
      <InputPrimitive
        ref={ref}
        id={id}
        type={type}
        data-slot="input"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          [
            "flex h-12 w-full min-w-0",
            "rounded-[var(--radius-md)] border",
            "bg-transparent px-[var(--space-4)] py-[var(--space-3)]",
            "text-[length:var(--font-size-base)]",
            "text-[var(--color-text-primary)]",
            "placeholder:text-[var(--color-text-muted)]",
            "transition-colors duration-[var(--duration-normal)]",
            "outline-none",
            // Default border
            "border-[var(--color-border-default)]",
            // Focus
            "focus-visible:border-[var(--color-border-focus)]",
            "focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
            // Error
            "aria-invalid:border-[var(--color-error)]",
            "aria-invalid:ring-[3px] aria-invalid:ring-[var(--color-error-muted)]",
            // Disabled
            "disabled:pointer-events-none disabled:cursor-not-allowed",
            "disabled:bg-[var(--color-bg-muted)] disabled:opacity-50",
            // File input
            "file:inline-flex file:h-6 file:border-0 file:bg-transparent",
            "file:text-[length:var(--font-size-sm)] file:font-[number:var(--font-weight-medium)]",
            "file:text-[var(--color-text-primary)]",
          ].join(" "),
          className
        )}
        {...props}
      />
    );

    // Bare input — no label/helper wrapper
    if (!label && !helperText && !error) {
      return inputElement;
    }

    return (
      <div className="flex flex-col gap-[var(--space-1-5)]">
        {label && (
          <label
            htmlFor={id}
            className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]"
          >
            {label}
            {optional && (
              <span className="ml-1 text-[length:var(--font-size-xs)] font-[number:var(--font-weight-normal)] text-[var(--color-text-muted)]">
                (optional)
              </span>
            )}
          </label>
        )}
        {inputElement}
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="text-[length:var(--font-size-xs)] text-[var(--color-error)]"
          >
            {error}
          </p>
        ) : helperText ? (
          <p
            id={helperId}
            className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]"
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
export type { InputProps };
